<script lang="ts" setup>
// -------------------------------
// 🔹 Imports Vue & composables
// -------------------------------
import { ref } from 'vue';
import { form, isLoaderLoginFormActivated } from '../../refs/login.form.refs';

const showOptional = ref(false);

// -------------------------------
// 🔹 Composants
// -------------------------------
import TextInput from '../common/BaseInput.vue';
import BaseButtonPrimary from '../common/BaseButtonPrimary.vue';

// -------------------------------
// 🔹 Mutations / services
// -------------------------------
import { CommerceMutation } from '@/mutations/commerce.mutations';
const {
  handleCreateCommerce,
  handleCreateCommerceContact,
  handleCreateCommerceLogo,
} = CommerceMutation();

// -------------------------------
// 🔹 Imports stores Pinia
// -------------------------------
import { useCommerceStore } from '@/store/commerce.store';
import { useDialogStore } from '@/store/dialog.store';

// -------------------------------
// 🔹 Imports assets / refs / données
// -------------------------------
import {
  idToDelete,
  isStepTwoActivated,
  previewUrl,
  isStepThreeActivated,
  file,
  form_CommerceBody,
} from '@/refs/commerce.refs';
import { content, isGlobalError, restaurantId } from '@/refs/common.refs';

// -------------------------------
// 🔹 Stores instanciés
// -------------------------------
const commerceStore = useCommerceStore();
const dialogStore = useDialogStore();

// -------------------------------
// 🔹 Émission d’événements
// -------------------------------
const emit = defineEmits<{
  (e: 'HandleClose'): void;
}>();

// -------------------------------
// 🔹 Gestion du formulaire
// -------------------------------

/**
 * Soumet le formulaire de création de commerce.
 * Gère les différentes étapes : création du commerce, ajout du contact, ou upload du logo.
 */
const handleSubmit = async () => {
  isLoaderLoginFormActivated.value = true;

  if (!isStepTwoActivated.value) {
    try {
      isLoaderLoginFormActivated.value = true;
      const resp = handleCreateCommerce({
        designation: form_CommerceBody.designation,
        orderCode: form_CommerceBody.orderCode,
        currency: form_CommerceBody?.currency,
        timezone: form_CommerceBody?.timezone,
        country: form_CommerceBody.country || undefined,
        bannerDescription:
          form_CommerceBody.bannerDescription?.fr ||
          form_CommerceBody.bannerDescription?.en ||
          form_CommerceBody.bannerDescription?.nl
            ? (form_CommerceBody.bannerDescription as any)
            : undefined,
        metadata:
          form_CommerceBody.metadata?.city ||
          form_CommerceBody.metadata?.foundedYear
            ? {
                city: form_CommerceBody.metadata?.city || undefined,
                foundedYear:
                  form_CommerceBody.metadata?.foundedYear ?? undefined,
              }
            : undefined,
      });

      if (resp) {
        setTimeout(() => {
          isLoaderLoginFormActivated.value = false;
          dialogStore.closeDialog();
        }, 1000);
      }
    } catch (error) {
      isGlobalError.value = true;
      setTimeout(() => {
        isGlobalError.value = false;
        isLoaderLoginFormActivated.value = false;
        dialogStore.closeDialog();
      }, 1000);
    }
  } else if (isStepTwoActivated.value) {
    handleCreateCommerceContact({
      type: 'phone',
      content: form_CommerceBody.contact,
    });
  } else {
    // Envoyer les champs optionnels de l'étape 3 via une mise à jour du commerce
    // puis uploader le logo si présent
    if (file.value) {
      handleCreateCommerceLogo({
        designation: 'logo du restaurant',
        restaurantId: restaurantId.value,
        file: file.value,
      });
    }
  }

  setTimeout(() => {
    isLoaderLoginFormActivated.value = false;
  }, 1000);
};

/**
 * Ferme le formulaire et réinitialise l’ID à supprimer.
 */
const handleClose = (): void => {
  idToDelete.value = null;
  emit('HandleClose');
};

/**
 * Gère l’upload du fichier pour le logo du commerce.
 * @param {Event} event Événement de changement sur le input file
 */
const handleFileUpload = (event: Event): void => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files[0]) {
    file.value = target.files[0];
    previewUrl.value = URL.createObjectURL(file.value);
  }
};
</script>

<template>
  <form
    autocomplete="on"
    accept-charset="UTF-8"
    @submit.prevent="handleSubmit"
    class="space-y-6"
  >
    <div class="space-y-[16px]" v-if="!isStepTwoActivated">
      <TextInput
        required="true"
        v-model="form_CommerceBody.designation"
        name="Entrez le nom du restaurant"
        placeholder="Entrez le nom du restaurant"
        type="text"
        id="Entrez le nom du restaurant"
        label="Entrez le nom du restaurant"
      />

      <TextInput
        required="true"
        v-model="form_CommerceBody.orderCode"
        name="Entrez l'abréviation du restaurant"
        placeholder="Entrez l'abréviation du restaurant"
        type="text"
        id="Entrez l'abréviation du restaurant"
        label="Entrez l'abréviation du restaurant"
      />

      <div>
        <label for="currency" class="mb-1 block font-medium text-gray-700">
          Sélectionnez la devise du restaurant
        </label>

        <select
          v-model="form_CommerceBody.currency"
          id="currency"
          name="currency"
          class="border-border-input w-full rounded-md border border-solid px-3 py-2 focus-within:border focus-within:border-solid focus:outline-none"
        >
          <option disabled value="">Sélectionnez une devise</option>
          <option value="EUR">Euro (EUR)</option>
          <option value="USD">Dollar US (USD)</option>
          <option value="GBP">Livre Sterling (GBP)</option>
        </select>
      </div>

      <div>
        <label for="timezone" class="mb-1 block font-medium text-gray-700">
          Entrez le fuseau horaire du restaurant
        </label>

        <select
          v-model="form_CommerceBody.timezone"
          id="timezone"
          name="timezone"
          class="border-border-input w-full rounded-md border border-solid px-3 py-2 focus-within:border focus-within:border-solid focus:outline-none"
        >
          <option disabled value="">Sélectionnez un fuseau horaire</option>
          <option value="Europe/Brussels">Europe/Brussels (UTC+1)</option>

          <option value="America/New_York">America/New_York (UTC-5)</option>
          <option value="UTC">UTC</option>
        </select>
      </div>
      <!-- Accordéon champs optionnels -->
      <div>
        <button
          type="button"
          @click="showOptional = !showOptional"
          class="flex w-full items-center justify-between rounded-md border border-dashed border-gray-200 px-3 py-2 text-sm text-gray-500 transition-colors hover:bg-gray-50"
        >
          <span>Informations supplémentaires (optionnel)</span>
          <svg
            :class="[
              'transition-transform duration-200',
              showOptional ? 'rotate-180' : '',
            ]"
            width="16"
            height="16"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke="#9CA3AF"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>

        <div v-if="showOptional" class="mt-3 space-y-[14px]">
          <!-- Pays -->
          <div>
            <label
              for="country"
              class="mb-1 block text-sm font-medium text-gray-700"
              >Pays</label
            >
            <select
              v-model="form_CommerceBody.country"
              id="country"
              class="border-border-input w-full rounded-md border border-solid px-3 py-2 text-sm focus:outline-none"
            >
              <option value="">— Sélectionner —</option>
              <option value="BE">Belgique</option>
              <option value="FR">France</option>
              <option value="LU">Luxembourg</option>
              <option value="NL">Pays-Bas</option>
              <option value="DE">Allemagne</option>
              <option value="GB">Royaume-Uni</option>
              <option value="US">États-Unis</option>
              <option value="CA">Canada</option>
              <option value="CH">Suisse</option>
              <option value="ES">Espagne</option>
              <option value="IT">Italie</option>
              <option value="MA">Maroc</option>
              <option value="SN">Sénégal</option>
              <option value="CI">Côte d'Ivoire</option>
            </select>
          </div>

          <!-- Description bannière -->
          <div>
            <label
              for="bannerDescription"
              class="mb-1 block text-sm font-medium text-gray-700"
              >Description bannière</label
            >
            <textarea
              :value="form_CommerceBody.bannerDescription?.fr ?? ''"
              @input="
                (e) => {
                  if (form_CommerceBody.bannerDescription)
                    form_CommerceBody.bannerDescription.fr = (
                      e.target as HTMLTextAreaElement
                    ).value;
                }
              "
              id="bannerDescription"
              rows="2"
              placeholder="Courte description affichée sur la bannière..."
              class="border-border-input w-full resize-none rounded-md border border-solid px-3 py-2 text-sm focus:outline-none"
            />
          </div>

          <!-- Ville -->
          <div>
            <label
              for="metaCity"
              class="mb-1 block text-sm font-medium text-gray-700"
              >Ville / localisation</label
            >
            <input
              v-model="form_CommerceBody.metadata!.city"
              id="metaCity"
              type="text"
              placeholder="ex: Bruxelles"
              class="border-border-input w-full rounded-md border border-solid px-3 py-2 text-sm focus:outline-none"
            />
          </div>

          <!-- Année de fondation -->
          <div>
            <label
              for="metaYear"
              class="mb-1 block text-sm font-medium text-gray-700"
              >Année de fondation</label
            >
            <input
              v-model.number="form_CommerceBody.metadata!.foundedYear"
              id="metaYear"
              type="number"
              placeholder="ex: 2018"
              min="1900"
              class="border-border-input w-full rounded-md border border-solid px-3 py-2 text-sm focus:outline-none"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="isStepThreeActivated" class="space-y-[16px]">
      <!-- Logo -->
      <div>
        <label for="logo" class="mb-1 block font-medium text-gray-700">
          Logo du restaurant
          <span class="text-sm text-gray-400">(optionnel)</span>
        </label>
        <input
          type="file"
          id="logo"
          accept="image/*"
          @change="handleFileUpload"
          class="border-border-input w-full rounded-md border px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
        />
        <div v-if="previewUrl" class="mt-2">
          <img
            :src="previewUrl"
            alt="Aperçu du logo"
            class="h-24 w-auto rounded-md object-contain"
          />
        </div>
      </div>

      <!-- Pays -->
      <div>
        <label for="country" class="mb-1 block font-medium text-gray-700">
          Pays <span class="text-sm text-gray-400">(optionnel)</span>
        </label>
        <select
          v-model="form_CommerceBody.country"
          id="country"
          class="border-border-input w-full rounded-md border border-solid px-3 py-2 focus:outline-none"
        >
          <option value="">Sélectionnez un pays</option>
          <option value="BE">Belgique</option>
          <option value="FR">France</option>
          <option value="LU">Luxembourg</option>
          <option value="NL">Pays-Bas</option>
          <option value="DE">Allemagne</option>
          <option value="GB">Royaume-Uni</option>
          <option value="US">États-Unis</option>
          <option value="CA">Canada</option>
          <option value="CH">Suisse</option>
          <option value="ES">Espagne</option>
          <option value="IT">Italie</option>
          <option value="MA">Maroc</option>
          <option value="SN">Sénégal</option>
          <option value="CI">Côte d'Ivoire</option>
        </select>
      </div>

      <!-- Description bannière -->
      <div>
        <label
          for="bannerDescription"
          class="mb-1 block font-medium text-gray-700"
        >
          Description bannière
          <span class="text-sm text-gray-400">(optionnel)</span>
        </label>
        <textarea
          :value="form_CommerceBody.bannerDescription?.fr ?? ''"
          @input="
            (e) => {
              if (form_CommerceBody.bannerDescription)
                form_CommerceBody.bannerDescription.fr = (
                  e.target as HTMLTextAreaElement
                ).value;
            }
          "
          id="bannerDescription"
          rows="3"
          placeholder="Courte description affichée sur la bannière..."
          class="border-border-input w-full resize-none rounded-md border border-solid px-3 py-2 focus:outline-none"
        />
      </div>

      <!-- Metadata : ville -->
      <div>
        <label for="metaCity" class="mb-1 block font-medium text-gray-700">
          Ville / localisation
          <span class="text-sm text-gray-400">(optionnel)</span>
        </label>
        <input
          v-model="form_CommerceBody.metadata!.city"
          id="metaCity"
          type="text"
          placeholder="ex: Bruxelles"
          class="border-border-input w-full rounded-md border border-solid px-3 py-2 focus:outline-none"
        />
      </div>

      <!-- Metadata : année de fondation -->
      <div>
        <label for="metaYear" class="mb-1 block font-medium text-gray-700">
          Année de fondation
          <span class="text-sm text-gray-400">(optionnel)</span>
        </label>
        <input
          v-model.number="form_CommerceBody.metadata!.foundedYear"
          id="metaYear"
          type="number"
          placeholder="ex: 2018"
          min="1900"
          :max="new Date().getFullYear()"
          class="border-border-input w-full rounded-md border border-solid px-3 py-2 focus:outline-none"
        />
      </div>
    </div>
    <div class="space-y-[16px]" v-else>
      <TextInput
        required="true"
        v-model="form_CommerceBody.contact"
        name="Entrez le numéro de télephone du restaurant"
        placeholder="Entrez le numéro de téléphone du restaurant"
        type="text"
        id="Entrez le numéro de téléphone du restaurant"
        label="Entrez le numéro de téléphone du restaurant"
      />
    </div>

    <div class="invisible h-[50px]" />

    <div class="fixed bottom-0 left-0 w-full bg-white py-6">
      <div class="flex flex-row items-center justify-between gap-8 px-[16px]">
        <div class="w-full">
          <BaseButtonPrimary
            :isClicked="isLoaderLoginFormActivated"
            type="submit"
            :label="isStepTwoActivated ? 'Términer' : 'Enregistrer'"
            style_of_btn="primary_color_of_btn "
            class="w-full text-white"
          />
        </div>

        <div class="w-full" @click="handleClose">
          <BaseButtonPrimary
            type="button"
            :label="'Annuler'"
            style_of_btn="secondary_color_of_btn "
            class="text-grand-text w-full"
          />
        </div>
      </div>
    </div>
  </form>
</template>
